/**
 * service-order-request.ts
 * kernel-cross-tenant-service-write, design.md "Interfaces / Contracts" —
 * shared Zod contracts for `servicios.service_order_request`, the
 * cross-tenant staging entity (XT1).
 *
 * `PartnerServiceOrderRequestCreateSchema` deliberately has NO
 * `clientId`/`companyId`/`counterpartyCompanyId` field — all three are
 * server-derived (XT4 for clientId; TenantContext for the other two).
 *
 * Spec: kernel-cross-tenant-service-order-create / all requirements
 * Design: design.md "Interfaces / Contracts"
 * Tasks: kernel-cross-tenant-service-write, Phase 6, task 6.1.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Create (partner-side)
// ---------------------------------------------------------------------------

export const PartnerServiceOrderRequestCreateSchema = z.object({
  ownerCompanyId: z.string().uuid(),
  serviceTypeId: z.string().uuid(),
  idempotencyKey: z.string().regex(/^[A-Za-z0-9._:-]{8,128}$/),
  wellName: z.string().max(256).optional(),
  location: z.string().max(256).optional(),
  notes: z.string().max(2000).optional(),
  scheduledAt: z.string().datetime().optional(),
  fields: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
});

export type PartnerServiceOrderRequestCreateBody = z.infer<typeof PartnerServiceOrderRequestCreateSchema>;

// ---------------------------------------------------------------------------
// Response DTO — the request row, both sides
// ---------------------------------------------------------------------------

export const ServiceOrderRequestStatusSchema = z.enum(['pending', 'accepted', 'rejected']);
export type ServiceOrderRequestStatus = z.infer<typeof ServiceOrderRequestStatusSchema>;

export const ServiceOrderRequestResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  counterpartyCompanyId: z.string().uuid(),
  serviceTypeId: z.string().uuid(),
  clientId: z.string().uuid(),
  requestedByUserId: z.string().uuid(),
  idempotencyKey: z.string(),
  status: ServiceOrderRequestStatusSchema,
  requestedFields: z.record(z.union([z.string(), z.number(), z.boolean()])),
  wellName: z.string().nullable(),
  location: z.string().nullable(),
  notes: z.string().nullable(),
  scheduledAt: z.string().datetime().nullable(),
  decidedByUserId: z.string().uuid().nullable(),
  decidedAt: z.string().datetime().nullable(),
  decisionNote: z.string().nullable(),
  createdServiceOrderId: z.string().uuid().nullable(),
  requestedAt: z.string().datetime(),
});

export type ServiceOrderRequestResponse = z.infer<typeof ServiceOrderRequestResponseSchema>;

/**
 * Inbox-list-only variant — adds `counterpartyCompanyName`, resolved
 * server-side via the kernel's published `lookupCompanyById` read port
 * (same "otherCompanyName" pattern `partnership.routes.ts` already uses),
 * so the owner inbox never renders a raw `counterpartyCompanyId` uuid
 * (apps/frontend/CLAUDE.md "no raw machine value... where a human label
 * belongs"). Deliberately NOT added to `ServiceOrderRequestResponseSchema`
 * itself — create/accept/reject responses have no UI surface that needs it
 * and adding it there would touch every Phase 4-6 fixture for no reader.
 */
export const ServiceOrderRequestInboxItemSchema = ServiceOrderRequestResponseSchema.extend({
  counterpartyCompanyName: z.string().nullable(),
});
export type ServiceOrderRequestInboxItem = z.infer<typeof ServiceOrderRequestInboxItemSchema>;

/**
 * Phase 7 bugfix (caught building the frontend consumer): the GET
 * /servicios/service-order-requests route (Phase 6) has always returned
 * `totalCount`/`page`/`pageSize`/`totalPages` alongside `items` (mirrors
 * every other paginated servicios list response) — this schema only
 * declared `items` until now, so it silently under-specified the route's
 * real contract. No route code changes; this is the schema catching up to
 * what was already being sent. See `service-order-request.routes.spec.ts`'s
 * GET describe block for the regression assertion.
 */
export const ServiceOrderRequestListResponseSchema = z.object({
  items: z.array(ServiceOrderRequestInboxItemSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

export type ServiceOrderRequestListResponse = z.infer<typeof ServiceOrderRequestListResponseSchema>;

// ---------------------------------------------------------------------------
// Accept / reject (owner-side)
// ---------------------------------------------------------------------------

export const RejectServiceOrderRequestBodySchema = z.object({
  decisionNote: z.string().max(2000).optional(),
});

export type RejectServiceOrderRequestBody = z.infer<typeof RejectServiceOrderRequestBodySchema>;

// ---------------------------------------------------------------------------
// Servicios-local error codes for this mechanism (kernel-owned errors —
// PARTNERSHIP_NOT_ACTIVE, PARTNER_CLIENT_NOT_RESOLVED, NOT_AN_AUTHORIZED_PARTNER
// — already live in @suite/contracts-kernel's ERROR_CODES; this constant
// covers only the servicios-originated one).
// ---------------------------------------------------------------------------

export const SERVICE_ORDER_REQUEST_ERROR_CODES = {
  SERVICE_ORDER_REQUEST_NOT_FOUND: 'SERVICE_ORDER_REQUEST_NOT_FOUND',
} as const;

// ---------------------------------------------------------------------------
// locx-voz-servicio-cross-tenant — cross-tenant read routes (D5, D6):
// "which contractor?" + "the owner's service-type catalog" + "my sent
// requests". Mirrors GET /locx/work-permits/contractors verbatim (design.md
// D6) — never a raw companyId without its resolved name.
// ---------------------------------------------------------------------------

export const PartnerContractorItemSchema = z.object({
  companyId: z.string().uuid(),
  name: z.string(),
});
export type PartnerContractorItem = z.infer<typeof PartnerContractorItemSchema>;

export const PartnerContractorsResponseSchema = z.object({
  items: z.array(PartnerContractorItemSchema),
});
export type PartnerContractorsResponse = z.infer<typeof PartnerContractorsResponseSchema>;

/** Owner-scoped service-type catalog entry — `{ id, code, label }`, same shape service-type-admin already exposes. */
export const PartnerServiceTypeItemSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
});
export type PartnerServiceTypeItem = z.infer<typeof PartnerServiceTypeItemSchema>;

export const PartnerServiceTypesResponseSchema = z.object({
  items: z.array(PartnerServiceTypeItemSchema),
});
export type PartnerServiceTypesResponse = z.infer<typeof PartnerServiceTypesResponseSchema>;

/**
 * "Mis solicitudes enviadas" — D5: rows live under the OWNER's `company_id`;
 * this is the operadora's own counterparty-scoped read of what SHE sent.
 * Adds `ownerCompanyName` (resolved via `lookupCompanyById`, same pattern as
 * `ServiceOrderRequestInboxItemSchema`'s `counterpartyCompanyName`) and
 * `serviceTypeLabel` (resolved via ONE `runAsPartnerReadTx` per distinct
 * owner, `null` on a degraded/revoked partnership) — never a raw uuid where
 * a human label belongs.
 */
export const MySentServiceOrderRequestItemSchema = ServiceOrderRequestResponseSchema.extend({
  ownerCompanyName: z.string().nullable(),
  serviceTypeLabel: z.string().nullable(),
});
export type MySentServiceOrderRequestItem = z.infer<typeof MySentServiceOrderRequestItemSchema>;

export const MySentServiceOrderRequestsResponseSchema = z.object({
  items: z.array(MySentServiceOrderRequestItemSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});
export type MySentServiceOrderRequestsResponse = z.infer<typeof MySentServiceOrderRequestsResponseSchema>;
